import React from 'react';
import './List.css';

/**
 * Baustein 5 — List.
 * Vertikale Liste mit Surface-Cards als Items. Optional mit Avatar und Inline-Actions.
 *
 * Komposition:
 *   <List>
 *     <List.Item>
 *       <List.Avatar>A</List.Avatar>
 *       <List.Main>
 *         <List.Name>Anna Schmidt</List.Name>
 *         <List.Meta>Online · 12 Decks · 412 Karten</List.Meta>
 *       </List.Main>
 *       <List.Actions>
 *         <button className="btn-secondary">Decks ansehen</button>
 *       </List.Actions>
 *     </List.Item>
 *   </List>
 *
 * Klickbare Zeilen: `<List.Item onClick={…}>` macht die Zeile zur Aktion (Auswahl-Listen,
 * z.B. die Bottom-Sheets des mobilen Deck-Editors). Der Zeilen-Inhalt wandert dafür in ein
 * echtes `<button>` — Fokus, Enter/Space und die Rolle im Accessibility-Baum liefert damit
 * der Browser. `List.Actions` bleibt Geschwister dieses Knopfes und behält seine eigenen
 * Bedienelemente (ein Knopf IM Knopf wäre ungültiges Markup und nicht fokussierbar).
 * Ohne `onClick` bleibt die Zeile ein reines `<li>`.
 *
 * `disabled` sperrt eine klickbare Zeile (z.B. solange eine Aktion läuft): der Zeilen-Knopf ist
 * `disabled` und die Zeile gedimmt. `List.Actions` bleiben davon unberührt, ihre Knöpfe
 * sperrt der Aufrufer selbst. Ohne `onClick` hat `disabled` keine Wirkung.
 *
 * Überlauf: `List.Name` und `List.Meta` bleiben einzeilig und kürzen mit Ellipsis; die
 * Actions-Spalte schrumpft nicht, damit ihre Knöpfe tippbar bleiben.
 *
 * Unterhalb von 900px (Mobil-Grenze der App) darf die Zeile umbrechen: `List.Main` hält
 * dabei mindestens 40 % der Zeilenbreite — ohne diese Grenze drückt der Flex-Kontext den
 * Namen auf null und die Actions ragen aus der Karte. Reicht der Platz nicht, rutscht die
 * Actions-Spalte rechtsbündig in eine zweite Zeile und bricht dort selbst um.
 */
export default function List({ children }) {
  return <ul className="list">{children}</ul>;
}

/**
 * Ein Listen-Eintrag — flexible Komposition mit Avatar/Main/Actions. Mit `onClick` wird
 * die Zeile selbst bedienbar, mit `disabled` gesperrt (siehe Kopf-Doku).
 */
List.Item = function ListItem({ children, onClick, disabled = false }) {
  if (!onClick) {
    return <li className="list__item">{children}</li>;
  }

  const kids = React.Children.toArray(children);
  const actions = kids.filter((child) => React.isValidElement(child) && child.type === List.Actions);
  const rowContent = kids.filter((child) => !actions.includes(child));

  return (
    <li className={`list__item list__item--clickable${disabled ? ' list__item--disabled' : ''}`}>
      <button type="button" className="list__item-button" onClick={onClick} disabled={disabled}>
        {rowContent}
      </button>
      {actions}
    </li>
  );
};

/** Avatar — kreisrunder Accent-Badge, Initiale oder Icon als Child. */
List.Avatar = function ListAvatar({ children }) {
  return <div className="list__avatar" aria-hidden>{children}</div>;
};

/** Main-Block — Name + Meta untereinander, füllt den verfügbaren Platz. */
List.Main = function ListMain({ children }) {
  return <div className="list__main">{children}</div>;
};

/** Primärer Name (eine Zeile, Ellipsis bei Überlauf). */
List.Name = function ListName({ children }) {
  return <div className="list__name">{children}</div>;
};

/** Sekundärer Meta-Text (kleiner, muted; eine Zeile, Ellipsis bei Überlauf). */
List.Meta = function ListMeta({ children }) {
  return <div className="list__meta">{children}</div>;
};

/** Inline-Actions rechts (Buttons, Icon-Buttons). */
List.Actions = function ListActions({ children }) {
  return <div className="list__actions">{children}</div>;
};
